/* 
  Human Risk Index v3.0 - Qualitative Strategic Scoring Engine
  Power Plays by Eric Stavola & Intelligent Provider Services
*/

// ------- Power Plays brand tokens -------
export const T = {
  ink: "#0D2440",       // logo navy
  navy: "#155A9C",      // deep brand blue
  navySoft: "#1E6FB2",  // logo 'RISK' blue
  teal: "#14AEC6",      // logo cyan accent
  slate: "#475569",
  muted: "#6B7280",
  paper: "#FFFFFF",
  bg: "#F2F6FA",
  rule: "#D9E2EC",
  lime: "#A4D82B",      // logo gauge lime
  limeDeep: "#7CB518",
  gold: "#C9A227",
  orange: "#C05621",
  danger: "#B23A2A",
};

export const SERIF = "'Montserrat', 'Segoe UI', system-ui, sans-serif";
export const SANS = "'Segoe UI', Calibri, system-ui, -apple-system, sans-serif";

export const METHODOLOGY_VERSION = "2024.1.Q";

/**
 * ------- Production Scoring Model -------
 */
export const SECTIONS = [
  {
    id: "phishing",
    name: "Phishing and Social Engineering",
    weight: 20,
    impact: "Reduces breach likelihood",
    narrative: "Measures workforce resilience against social engineering and deception.",
    questions: [
      {
        id: "phish_sim",
        text: "Do you run regular phishing simulations against your workforce?",
        opts: [["Monthly ongoing program", 0, 5], ["Quarterly or bi-annually", 30, 3], ["Ran once or twice", 70, 2], ["Never", 100, 1]],
        maturityContribution: 1.0
      },
      {
        id: "reporting_process",
        text: "Is there a defined process for employees to report suspicious communications?",
        opts: [["One-click button + automated analysis", 0, 5], ["Formal internal portal", 25, 4], ["Forward to IT helpdesk", 60, 2], ["No defined process", 100, 1]],
        maturityContribution: 1.0
      }
    ]
  },
  {
    id: "identity",
    name: "Identity and Credential Exposure",
    weight: 20,
    impact: "Prevents account takeover",
    narrative: "Evaluates the strength of access controls and credential hygiene.",
    questions: [
      {
        id: "mfa_enforcement",
        text: "Where is multi-factor authentication (MFA) strictly enforced?",
        opts: [["Zero Trust / All systems", 0, 5], ["Email and Cloud apps", 30, 3], ["Admin access only", 70, 2], ["Not enforced", 100, 1]],
        maturityContribution: 1.0
      },
      {
        id: "password_mgmt",
        text: "How are corporate passwords managed across the workforce?",
        opts: [["Enterprise-grade password manager", 0, 5], ["Browser-sync only", 40, 3], ["Informal/Spreadsheets", 80, 1], ["No managed solution", 100, 1]],
        maturityContribution: 1.0
      }
    ]
  },
  {
    id: "business_process",
    name: "Business Process Manipulation",
    weight: 15,
    impact: "Reduces wire fraud risk",
    narrative: "Focuses on Business Email Compromise (BEC) and financial authorization.",
    questions: [
      {
        id: "payment_verification",
        text: "What is the process for changing payment instructions or wire transfers?",
        opts: [["Out-of-band voice/visual verification", 0, 5], ["Formal digital dual-signoff", 30, 4], ["Single-person email approval", 80, 2], ["Informal process", 100, 1]],
        maturityContribution: 1.5
      },
      {
        id: "exec_impersonation",
        text: "How do employees handle 'urgent' out-of-channel requests from leadership?",
        opts: [["Strict verification protocol", 0, 5], ["Informal check-back", 40, 3], ["Usually follow orders", 90, 1]],
        maturityContribution: 0.5
      }
    ]
  },
  {
    id: "data_behavior",
    name: "Sensitive Data Behavior",
    weight: 15,
    impact: "Reduces data leakage",
    narrative: "Evaluates how employees handle, share, and store company secrets.",
    questions: [
      {
        id: "file_sharing",
        text: "How do employees share sensitive files with external vendors?",
        opts: [["Governed secure portal", 0, 5], ["Passworded attachments", 40, 3], ["Standard email", 80, 2], ["Personal cloud storage", 100, 1]],
        maturityContribution: 1.0
      }
    ]
  },
  {
    id: "ai_shadow",
    name: "AI and Shadow Technology",
    weight: 10,
    impact: "Governs emerging tech risk",
    narrative: "Measures risk from unapproved AI tools and unauthorized software.",
    questions: [
      {
        id: "ai_governance",
        text: "Is there a formal policy for corporate data usage in public AI tools?",
        opts: [["Strict policy + approved AI tools", 0, 5], ["General guidelines", 40, 3], ["No policy", 100, 1]],
        maturityContribution: 1.0
      }
    ]
  },
  {
    id: "workforce_lifecycle",
    name: "Workforce Lifecycle",
    weight: 10,
    impact: "Manages insider risk",
    narrative: "Assesses risks during onboarding, role changes, and offboarding.",
    questions: [
      {
        id: "offboarding_speed",
        text: "How quickly is system access terminated when an employee departs?",
        opts: [["Automated / Immediate", 0, 5], ["Within 24 hours", 30, 4], ["Within a week", 70, 2], ["Inconsistent", 100, 1]],
        maturityContribution: 1.0
      }
    ]
  },
  {
    id: "leadership_culture",
    name: "Leadership and Reporting Culture",
    weight: 10,
    impact: "Drives security alignment",
    narrative: "Measures executive commitment and security transparency.",
    questions: [
      {
        id: "security_awareness",
        text: "How often does leadership communicate security priorities to the team?",
        opts: [["Monthly board-level updates", 0, 5], ["Quarterly town halls", 30, 4], ["Annual training only", 70, 2], ["Rarely", 100, 1]],
        maturityContribution: 1.0
      }
    ]
  }
];

export const ALL_Q = SECTIONS.flatMap((s) => s.questions.map((q) => ({ ...q, section: s })));

export const TIERS = [
  { max: 24, name: "Controlled", color: T.limeDeep, meaning: "Core behaviors appear reasonably managed" },
  { max: 49, name: "Elevated", color: T.gold, meaning: "Targeted improvements recommended" },
  { max: 74, name: "High", color: T.orange, meaning: "Material workforce exposure likely exists" },
  { max: 100, name: "Critical", color: T.danger, meaning: "Immediate remediation required" },
];

export const MATURITY_LEVELS = [
  { level: 1, name: "Reactive", description: "No consistent process or named ownership." },
  { level: 2, name: "Defined", description: "Documented policies or processes exist." },
  { level: 3, name: "Managed", description: "Processes operate consistently with accountable owners." },
  { level: 4, name: "Measured", description: "Performance is measured through defined operational metrics." },
  { level: 5, name: "Adaptive", description: "Measurements trigger targeted interventions and process improvements." }
];

export const RESULT_NARRATIVES = {
  "Controlled": "Your organization demonstrates strong foundational security behaviors. The focus should shift to continuous measurement and advanced threat modeling.",
  "Elevated": "Your organization has basic controls, but specific behavioral gaps expose you to targeted attacks. Targeted improvements are recommended.",
  "High": "Your organization has elevated human-layer exposure. The primary concern is that current behaviors, approval workflows, and security habits may be easier for attackers to exploit than leadership realizes.",
  "Critical": "Your organization faces severe and immediate risk. Critical control failures in human processes leave the business highly vulnerable to compromise."
};

export const NEXT_MOVES = {
  "Controlled": "Schedule a quarterly review to maintain posture and evaluate advanced telemetry.",
  "Elevated": "Schedule a 30-minute Human Risk Review to address specific domain gaps.",
  "High": "Schedule a 30-minute Human Risk Review to convert this assessment into a 30-day remediation plan.",
  "Critical": "Immediate executive intervention required. Schedule an emergency risk briefing."
};

export const ROADMAPS = {
  "Controlled": [
    { phase: "Optimization", goals: ["Automate reporting", "Advanced simulation"] },
    { phase: "Resilience", goals: ["Continuous validation", "Threat intelligence integration"] }
  ],
  "Elevated": [
    { phase: "Targeted Remediation", goals: ["Address specific gaps", "Improve reporting culture"] },
    { phase: "Process Hardening", goals: ["Formalize workflows", "Expand MFA coverage"] }
  ],
  "High": [
    { phase: "Immediate Mitigation", goals: ["Enforce MFA globally", "Implement payment verification"] },
    { phase: "Behavioral Shift", goals: ["Launch awareness program", "Establish reporting channels"] }
  ],
  "Critical": [
    { phase: "Crisis Response", goals: ["Deploy emergency controls", "Halt high-risk practices"] },
    { phase: "Foundation Building", goals: ["Establish basic governance", "Deploy core identity controls"] }
  ]
};

/**
 * ATTACK PATH TRANSLATIONS
 */
export const ATTACK_PATH_TEMPLATES = {
  "no_mfa": {
    trigger: "Credential Harvesting / Phishing",
    failure: "Absence of MFA for Cloud/Email access",
    event: "Unauthorized Account Takeover (ATO)",
    impact: "Data exfiltration and lateral movement"
  },
  "no_bec_verification": {
    trigger: "Executive Impersonation / Vendor Invoice Fraud",
    failure: "Lack of out-of-band payment verification",
    event: "Fraudulent Wire Transfer Authorization",
    impact: "Direct financial loss and wire fraud"
  },
  "no_offboarding": {
    trigger: "Employee Departure",
    failure: "Inconsistent system access termination",
    event: "Persistence of 'Orphaned' Active Accounts",
    impact: "Unauthorized access to proprietary data"
  },
  "no_reporting": {
    trigger: "Targeted Phishing Attempt",
    failure: "No defined suspicious reporting process",
    event: "Undetected Threat Propagation",
    impact: "Workforce-wide compromise escalation"
  }
};

/**
 * CRITICAL CONTROL OVERRIDE RULES
 */
export const OVERRIDES = [
  {
    id: "no_mfa",
    condition: (ans) => ans.mfa_enforcement >= 2,
    floor: 65,
    domain: "Overall Exposure",
    reason: "No MFA for email or administrative access creates an unmanaged identity floor."
  },
  {
    id: "no_bec_verification",
    condition: (ans) => ans.payment_verification >= 2,
    floor: 80,
    domain: "Business Process Manipulation",
    reason: "Payment changes accepted through email without independent verification."
  },
  {
    id: "no_offboarding",
    condition: (ans) => ans.offboarding_speed >= 2,
    floor: 75,
    domain: "Workforce Lifecycle",
    reason: "No consistent employee offboarding process creates orphan account risk."
  },
  {
    id: "no_reporting",
    condition: (ans) => ans.reporting_process >= 3,
    floor: 65,
    domain: "Phishing and Social Engineering",
    reason: "No reporting process for suspicious communications prevents organizational learning."
  }
];

export function computeModel(answers) {
  const domainExposures = [];
  const domainMaturities = [];
  let totalAnswered = 0;
  let unknownCount = 0;

  SECTIONS.forEach(section => {
    let sectionExposureSum = 0;
    let sectionMaturitySum = 0;
    let sectionAnswered = 0;

    section.questions.forEach(q => {
      const ansIdx = answers[q.id];
      if (ansIdx !== undefined) {
        const [label, exposureValue, maturityValue] = q.opts[ansIdx];
        sectionExposureSum += exposureValue;
        sectionMaturitySum += maturityValue;
        sectionAnswered++;
        totalAnswered++;
        if (label.toLowerCase().includes("unknown") || label.toLowerCase().includes("not sure")) {
          unknownCount++;
        }
      }
    });

    const avgExposure = sectionAnswered > 0 ? sectionExposureSum / sectionAnswered : null;
    const avgMaturity = sectionAnswered > 0 ? sectionMaturitySum / sectionAnswered : 1;

    // Apply Domain-Specific Overrides
    let finalDomainExposure = avgExposure;
    const activeOverrides = OVERRIDES.filter(o => o.domain === section.name && o.condition(answers));
    activeOverrides.forEach(o => {
      if (finalDomainExposure !== null && finalDomainExposure < o.floor) {
        finalDomainExposure = o.floor;
      }
    });

    domainExposures.push({
      name: section.name,
      weight: section.weight,
      exposure: finalDomainExposure,
      maturity: avgMaturity,
      overrides: activeOverrides
    });

    domainMaturities.push(avgMaturity);
  });

  // Calculate Overall Exposure Score
  const activeDomains = domainExposures.filter(d => d.exposure !== null);
  const totalWeight = activeDomains.reduce((sum, d) => sum + d.weight, 0);
  let rawScore = totalWeight > 0 
    ? activeDomains.reduce((sum, d) => sum + (d.exposure * d.weight), 0) / totalWeight
    : 0;

  // Apply Overall Overrides
  const globalOverrides = OVERRIDES.filter(o => o.domain === "Overall Exposure" && o.condition(answers));
  globalOverrides.forEach(o => {
    if (rawScore < o.floor) rawScore = o.floor;
  });

  const finalScore = Math.round(rawScore);
  const tier = tierFor(finalScore);

  // Maturity
  const avgMaturity = domainMaturities.reduce((a, b) => a + b, 0) / domainMaturities.length;
  const minMaturity = Math.min(...domainMaturities);
  const finalMaturity = Math.min(Math.round(avgMaturity), Math.floor(minMaturity) + 1);

  // Confidence
  const completionPct = (totalAnswered / ALL_Q.length) * 100;
  const unknownPenalty = (unknownCount / ALL_Q.length) * 50;
  const finalConfidence = Math.max(0, Math.round(completionPct - unknownPenalty));

  // Attack Paths
  const attackPaths = globalOverrides.concat(domainExposures.flatMap(d => d.overrides))
    .map(o => ({
      ...ATTACK_PATH_TEMPLATES[o.id],
      reason: o.reason
    }))
    .filter(ap => ap.trigger);

  // Identity
  const assessmentId = `HRI-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

  return {
    score: finalScore,
    maturity: finalMaturity,
    confidence: finalConfidence,
    tier,
    dims: domainExposures.map(d => ({
      name: d.name,
      pct: d.exposure !== null ? d.exposure / 100 : null,
      maturity: d.maturity,
      overrides: d.overrides
    })),
    answered: totalAnswered,
    findings: globalOverrides.concat(domainExposures.flatMap(d => d.overrides)).map(o => ({
      id: o.id,
      view: o.reason,
      remedy: "Implement critical security controls to remove exposure floor.",
      sev: 3,
      owner: "CISO / IT Director",
      deadline: "30 Days",
      success: "Documented control implementation",
      status: "Required"
    })),
    attackPaths,
    identity: {
      id: assessmentId,
      version: METHODOLOGY_VERSION,
      type: "Strategic Pulse",
      status: totalAnswered === ALL_Q.length ? "Validated" : "Preliminary"
    },
    confidenceNotice: finalConfidence >= 80 ? "Validated" : finalConfidence >= 50 ? "Evidence-Supported" : "Self-Reported",
    executiveDecisions: [
      { decision: "Strategic Remediation Approval", owner: "CEO / Board", date: "TBD" },
      { decision: "Budget Allocation for Core Controls", owner: "CFO", date: "TBD" }
    ]
  };
}

export function tierFor(score) {
  return TIERS.find((t) => score <= t.max) || TIERS[TIERS.length - 1];
}

// ------- Shareable-link codec -------
const ORDER = ALL_Q.map((q) => q.id);

export function encodeAnswers(answers) {
  return ORDER.map((id) => (answers[id] === undefined ? "x" : String(answers[id]))).join("");
}

export function decodeAnswers(str) {
  if (!str || str.length !== ORDER.length) return null;
  const out = {};
  ORDER.forEach((id, i) => {
    const ch = str[i];
    if (ch === "x") return;
    const idx = parseInt(ch, 10);
    if (!Number.isNaN(idx) && idx >= 0 && idx < ALL_Q[i].opts.length) out[id] = idx;
  });
  return out;
}

export function shareUrl(answers) {
  const encoded = encodeAnswers(answers);
  const url = new URL(window.location.href);
  if (encoded.replace(/x/g, "").length === 0) {
    url.searchParams.delete("a");
  } else {
    url.searchParams.set("a", encoded);
  }
  return url.toString();
}

export const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "absolute";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  const success = document.execCommand("copy");
  document.body.removeChild(ta);
  return success;
};